import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import mixpanel from 'mixpanel-browser'
import Link from 'next/link'
import React, { Dispatch, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SignUpModal } from 'src/components/global/SignUpModal'
import { MIXPANEL_ENABLED } from 'src/config/config'
import {
  IAddSolutionToSharedPortfolioMutation,
  IAddSolutionToSharedPortfolioMutationVariables,
  IEnum_Solution_Type,
  IFollowSharedPortfolioSolutionMutation,
  IFollowSharedPortfolioSolutionMutationVariables,
  IFollowSolutionMutation,
  IFollowSolutionMutationVariables,
  IUpdateSharedPortfolioSolutionMutation,
  useAddSolutionToSharedPortfolioMutation,
  useFollowSharedPortfolioSolutionMutation,
  useFollowSolutionMutation,
  useGetSharedPortfoliosForMyOrganizationLazyQuery,
  useIsFollowLazyQuery,
  useUpdateSharedPortfolioSolutionMutation,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { showToast } from 'src/store/actions/toastAction'
import { AppState } from 'src/store/types'
import {
  isEmailInSharedPortfolioMembers,
  resolveFollowStateFromPortfolioEntries,
} from 'src/utils/sharedPortfolioMembership'

import { PortfolioPickerOption } from './CreateNewPortfolioModal'
import { FollowSolutionMeta, PremiumFollowSolution } from './PremiumFollowSolution'

interface FollowButtonProps {
  id: string
  solutionType?: IEnum_Solution_Type | null
  displayName?: string | null
  isEnhanced?: boolean | null
  isButton?: boolean
  className?: string
  follow?: boolean
  isFromVendorList?: boolean
  onRefresh?: (item: boolean) => void
  isFrom?: string
  followTypeName?: string | null
  dirctorySaredPortfolio?: boolean
  portfolioPickerOptions?: PortfolioPickerOption[]
  onSubmitSharedPortfolio?: () => void | Promise<void>
  sharedPortfolioId?: string
  portfolioSlug?: string
  solutionItem?: any
  sharedPortfolioSolutions?: any[]
  refreshFollowVendorList?: () => void
  styleParameters?: string
}

const FIGMA = {
  listWidthPx: 1100,
  rowHeightPx: 132,
  /** Article / caption (Figma “article copy 13”) */
  articleCopy13Px: 14,
  articleCopyLineHeightPx: 18,
  /** Portfolio item title (Figma: Poppins 700, 18px, line-height 100%) */
  listingTitlePx: 18,
  /** Heading row: title + more control */
  headingRowMinHeightPx: 27,
  /** Figma measured width of title + actions row inside 800px frame */
  headingRowMaxWidthPx: '100%', // 671.286,
  rowPaddingX: 24,
  /** Icon container — Figma desktop 80×80 */
  iconBoxPx: 80,
  /** User / users glyph inside tile (Figma: 40×40) */
  iconGlyphPx: 40,
  /** Gap after icon so title starts at ~110px from row left (24 + 80 + 6) */
  iconToTextGapPx: 30,
  /** Private / Shared chip — min width / min height (Figma); wider when label needs it */
  visibilityPillMinWidthPx: 106,
  visibilityPillMinHeightPx: 30,
  visibilityPillPaddingY: 8,
  visibilityPillPaddingX: 14,
  visibilityPillRadiusPx: 15,
  /** Figma neutral / lightest gray */
  visibilityPillBg: '#F1F3F5',
  /** Gap between heading row and pill (Figma: 8px) */
  titleToPillGapPx: 5,
  borderSubtle: '#E6E6E6',
  /** Unfollow row — native checkbox layout (Figma) */
  unfollowCheckboxSizePx: 10.5,
  unfollowCheckboxInsetPx: 1.75,
  /** Inset + size + inset */
  unfollowCheckboxFramePx: 14,
  unfollowCheckboxAccent: '#011D58',
} as const

let basicUserPrivatePortfolioSlugCache = ''
let premiumWithoutOrgPrivatePortfolioSlugCache = ''

/**
 * Primary UI component for user interaction
 */
export const FollowButton: React.FC<FollowButtonProps> = ({
  id,
  solutionType,
  displayName,
  isEnhanced = false,
  className,
  isButton = true,
  follow = false,
  isFromVendorList = false,
  onRefresh,
  isFrom = '',
  followTypeName = null,
  // dirctorySaredPortfolio = false,
  portfolioPickerOptions = [],
  onSubmitSharedPortfolio,
  sharedPortfolioId = undefined,
  portfolioSlug = '',
  // solutionItem = null,
  sharedPortfolioSolutions = [],
  refreshFollowVendorList,
  styleParameters = '',
}) => {
  const { status } = useAuthenticated()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: Dispatch<any> = useDispatch()

  const authInfo = useSelector((state: AppState) => state.authInfo)
  const userEmail = authInfo.email || authInfo.username || ''

  const filterPortfolioEntriesForUser = (entries: any[]) =>
    entries
      .filter(
        (entry) =>
          !entry?.members || entry?.portfolioId == null || isEmailInSharedPortfolioMembers(entry.members, userEmail),
      )
      .map((entry) => ({
        ...entry,
        isSharedPortfolio:
          typeof entry?.isSharedPortfolio === 'boolean'
            ? entry.isSharedPortfolio
            : !(typeof entry?.slug === 'string' && entry.slug.includes('my-portfolio')),
      }))
      .sort((a, b) => {
        if (a.isSharedPortfolio !== b.isSharedPortfolio) {
          return a.isSharedPortfolio ? 1 : -1
        }
        if (a.isSharedPortfolio) {
          return String(a?.name || '').localeCompare(String(b?.name || ''))
        }
        return 0
      })

  const [following, setFollowing] = useState(follow)
  const [showSignUp, setShowSignUp] = useState(false)
  const [showFollowType, setShowFollowType] = useState(false)
  const [localFollowTypeName, setLocalFollowTypeName] = useState<string | null>(followTypeName)
  const [isPremium, setIsPremium] = useState<boolean | null>(null)
  const [showFollowHoverPopup, setShowFollowHoverPopup] = useState(false)
  const [portfolioEntries, setPortfolioEntries] = useState<any[]>(
    filterPortfolioEntriesForUser(sharedPortfolioSolutions || []),
  )
  /** Ignores stale list `follow` prop until refetch confirms unfollow (fixes race after mutation). */
  const pendingFollowActionRef = useRef<'follow' | 'unfollow' | null>(null)
  const hoverPopupInstanceId = useId()
  const hoverPopupRef = useRef<HTMLDivElement>(null)
  const portfolioPopupRef = useRef<HTMLDivElement>(null)
  const hoverCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [canUseHoverInteraction, setCanUseHoverInteraction] = useState(true)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const hoverMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)')
    const syncHoverCapability = () => setCanUseHoverInteraction(hoverMediaQuery.matches)
    syncHoverCapability()
    hoverMediaQuery.addEventListener('change', syncHoverCapability)
    return () => hoverMediaQuery.removeEventListener('change', syncHoverCapability)
  }, [])

  const cancelHoverClose = () => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current)
      hoverCloseTimerRef.current = null
    }
  }

  const scheduleHoverClose = () => {
    cancelHoverClose()
    hoverCloseTimerRef.current = setTimeout(() => {
      setShowFollowHoverPopup(false)
      hoverCloseTimerRef.current = null
    }, 200)
  }

  const applyFollowUiState = (nextFollowing: boolean, nextFollowType: string | null = null) => {
    setFollowing(nextFollowing)
    setLocalFollowTypeName(nextFollowing ? nextFollowType : null)
    if (!nextFollowing) {
      cancelHoverClose()
      setShowFollowHoverPopup(false)
    }
  }
  const [loadIsFollowing, { data: isFollowingData }] = useIsFollowLazyQuery()
  const [followSolution] = useFollowSolutionMutation()
  const [followSharedPortfolioSolution] = useFollowSharedPortfolioSolutionMutation()
  const [addSolutionToSharedPortfolio] = useAddSolutionToSharedPortfolioMutation()
  const [updateSharedPortfolioSolution] = useUpdateSharedPortfolioSolutionMutation()
  const [loadSharedPortfolios] = useGetSharedPortfoliosForMyOrganizationLazyQuery()

  const hasPremiumOrganization = authInfo?.premiumOrganizationId != null && authInfo?.premiumOrganizationName != null
  const isPremiumWithoutOrganization = isPremium === true && !hasPremiumOrganization
  const isBasicUser = isPremium === false

  const canUseSharedPortfolio = isPremium === true && hasPremiumOrganization

  const getPrivatePortfolioSlugForCurrentUser = useCallback(() => {
    if (isPremiumWithoutOrganization) {
      return premiumWithoutOrgPrivatePortfolioSlugCache
    }
    if (isBasicUser) {
      return basicUserPrivatePortfolioSlugCache
    }
    return ''
  }, [isBasicUser, isPremiumWithoutOrganization])

  const fetchAndStorePrivatePortfolioSlug = useCallback(
    async (forceRefresh = false) => {
      if (!authInfo?.idToken || hasPremiumOrganization) {
        return ''
      }
      if (!isBasicUser && !isPremiumWithoutOrganization) {
        return ''
      }
      const cachedSlug = getPrivatePortfolioSlugForCurrentUser()
      if (!forceRefresh && cachedSlug) {
        return cachedSlug
      }

      try {
        const response = await loadSharedPortfolios({
          variables: {
            page: 1,
            pageSize: 25,
          },
          fetchPolicy: 'network-only',
        })
        let privatePortfolioSlug =
          response.data?.sharedPortfoliosForMyOrganization?.privateFollowingPortfolio?.slug || ''
        if (!privatePortfolioSlug) {
          const portfolios = response.data?.sharedPortfoliosForMyOrganization?.portfolios || []
          for (const portfolioEntry of portfolios) {
            if (!portfolioEntry?.attributes?.isSharedPortfolio && portfolioEntry?.attributes?.slug) {
              privatePortfolioSlug = portfolioEntry.attributes.slug
              break
            }
          }
        }

        if (isPremiumWithoutOrganization) {
          premiumWithoutOrgPrivatePortfolioSlugCache = privatePortfolioSlug
        } else if (isBasicUser) {
          basicUserPrivatePortfolioSlugCache = privatePortfolioSlug
        }

        return privatePortfolioSlug
      } catch {
        if (isPremiumWithoutOrganization) {
          premiumWithoutOrgPrivatePortfolioSlugCache = ''
        } else if (isBasicUser) {
          basicUserPrivatePortfolioSlugCache = ''
        }
        return ''
      }
    },
    [
      authInfo?.idToken,
      getPrivatePortfolioSlugForCurrentUser,
      hasPremiumOrganization,
      isBasicUser,
      isPremiumWithoutOrganization,
      loadSharedPortfolios,
    ],
  )

  const modalPortfolioPickerOptions = useMemo(
    () => (hasPremiumOrganization ? portfolioPickerOptions : []),
    [hasPremiumOrganization, portfolioPickerOptions],
  )

  useEffect(() => {
    if (pendingFollowActionRef.current === 'unfollow' && follow === true) {
      return
    }
    if (!follow) {
      setLocalFollowTypeName(null)
      return
    }
    if (followTypeName) {
      setLocalFollowTypeName(followTypeName)
    }
  }, [followTypeName, follow])

  useEffect(() => {
    if (
      !isFromVendorList &&
      authInfo.idToken &&
      authInfo.idToken !== null &&
      authInfo.idToken !== '' &&
      authInfo.id &&
      authInfo.id !== null &&
      authInfo.id !== ''
    ) {
      loadIsFollowing({
        variables: { id: id },
        context: { headers: { Authorization: `Bearer ${authInfo.idToken}` } },
      })
    }
    if (
      authInfo.idToken &&
      authInfo.idToken !== null &&
      authInfo.idToken !== '' &&
      authInfo.id &&
      authInfo.id !== null &&
      authInfo.id !== ''
    ) {
      setIsPremium(authInfo?.isPremium || false)
    }
  }, [authInfo])

  useEffect(() => {
    if (!isFromVendorList) {
      if (isFollowingData?.isFollow === 'true') {
        setFollowing(true)
      } else if (['Portfolio', 'Reviewed', 'Watching'].includes(isFollowingData?.isFollow || '')) {
        setFollowing(true)
        setLocalFollowTypeName(isFollowingData?.isFollow || null)
      } else {
        setFollowing(false)
      }
    }
  }, [isFollowingData])

  useEffect(() => {
    if (typeof follow !== 'boolean') return
    if (pendingFollowActionRef.current === 'unfollow' && follow === true) {
      return
    }
    if (follow === false) {
      pendingFollowActionRef.current = null
    }
    setFollowing(follow)
    if (!follow) {
      setLocalFollowTypeName(null)
    }
  }, [follow])

  useEffect(() => {
    setPortfolioEntries(filterPortfolioEntriesForUser(sharedPortfolioSolutions || []))
  }, [sharedPortfolioSolutions, userEmail])
  const onHandleFollow = async (followType?: string | null, portfolioId: string = '', meta?: FollowSolutionMeta) => {
    if (status !== 'authenticated') {
      setShowSignUp(true)
      return
    }
    if (
      isPremium &&
      !['Portfolio', 'Reviewed', 'Watching'].includes(followType || '') &&
      (!following || (isFrom === '' && following && hasPremiumOrganization))
    ) {
      setShowFollowType(true)
      return
    }

    if (authInfo.idToken !== undefined && authInfo.idToken !== '') {
      const isSharedPortfolioPageUnfollow =
        ['my-private-portfolio', 'my-shared-portfolio', 'custom-tag'].includes(isFrom) && following
      if (isSharedPortfolioPageUnfollow || (meta?.isDeleted && meta?.portfolioSolutionSlug)) {
        try {
          const portfolioSlugToUse = portfolioSlug || meta?.portfolioSolutionSlug
          const { data } = await followSharedPortfolioSolution({
            variables: {
              portfolioSlug: portfolioSlugToUse,
              solutionId: id,
              follow: false,
              updateType: 'Remove',
            },
          })

          if (data?.followSharedPortfolioSolution) {
            if (meta?.showDeleteSuccessToast) {
              dispatch(
                showToast(
                  'Updated your Portfolio',
                  ['my-private-portfolio', 'my-following', 'my-shared-portfolio', 'custom-tag'].includes(isFrom)
                    ? 'confirm_only_no_link'
                    : 'confirm',
                  {
                    viewAllHref: '/my-portfolios',
                  },
                ),
              )
            }
            if (isSharedPortfolioPageUnfollow) {
              pendingFollowActionRef.current = 'unfollow'
              applyFollowUiState(false, null)
              dispatch(
                showToast(
                  isPremium ? 'Removed from your Portfolio' : 'Removed from your followed solutions',
                  ['my-private-portfolio', 'my-following', 'my-shared-portfolio', 'custom-tag'].includes(isFrom)
                    ? 'confirm_only_no_link'
                    : 'confirm',
                  { viewAllHref: '/my-portfolios' },
                ),
              )
            }

            if (typeof refreshFollowVendorList === 'function') {
              await refreshFollowVendorList()
            } else if (onRefresh && typeof onRefresh === 'function') {
              onRefresh(true)
            }

            if (MIXPANEL_ENABLED) {
              mixpanel.track('unfollow_product', {
                solutionType,
                id,
                displayName,
                isEnhanced,
              })
            }
          }
        } catch (error) {
          const errorMessage =
            (
              error as {
                graphQLErrors?: Array<{ message?: string }>
                networkError?: { message?: string }
                message?: string
              }
            )?.graphQLErrors?.[0]?.message ||
            (error as { networkError?: { message?: string } })?.networkError?.message ||
            (error as { message?: string })?.message ||
            'An error occurred while processing your request. Please try again.'

          dispatch(showToast(errorMessage, 'error'))
        }
        return
      }

      const effectivePortfolioId = canUseSharedPortfolio && portfolioId && portfolioId !== '' ? portfolioId : ''

      const isPortfolioTypeUpdate = Boolean(meta?.portfolioUpdated)
      const isAdditiveSharedPortfolio =
        Boolean(effectivePortfolioId) && following && isFrom === '' && !isPortfolioTypeUpdate

      let followSolutionVariables:
        | IFollowSolutionMutationVariables
        | IAddSolutionToSharedPortfolioMutationVariables
        | IFollowSharedPortfolioSolutionMutationVariables

      if (!effectivePortfolioId && isPremium) {
        // Premium without org: private portfolio via shared-portfolio alias `_private_portfolio`
        if (!following || (Boolean(followType) && followType != 'remove')) {
          followSolutionVariables = {
            sharedID: '_private_portfolio',
            solution: id,
            category: followType || '',
            followingSolutionNote: null,
          }
        } else {
          followSolutionVariables = {
            portfolioSlug: '_private_portfolio',
            solutionId: id,
            follow: false,
            updateType: 'Remove',
          }
        }
      } else if (effectivePortfolioId && isPremium && !following) {
        followSolutionVariables = {
          sharedID: effectivePortfolioId,
          category: followType || '',
          solution: id,
          followingSolutionNote: null,
        }
      } else if (isAdditiveSharedPortfolio) {
        followSolutionVariables = {
          sharedID: effectivePortfolioId,
          category: followType || '',
          solution: id,
          followingSolutionNote: null,
        }
      } else {
        // Basic user (and remaining private paths): private portfolio via `_private_portfolio`
        followSolutionVariables = {
          sharedID: '_private_portfolio',
          solution: id,
          category: 'Following',
          followingSolutionNote: null,
        }
      }

      let mutationIsFollow = effectivePortfolioId
        ? true
        : Boolean((followSolutionVariables as IAddSolutionToSharedPortfolioMutationVariables).category)

      let portfolioSolutionId: string | null = null
      if (meta?.portfolioSolutionData && Array.isArray(meta.portfolioSolutionData)) {
        portfolioSolutionId =
          (
            meta.portfolioSolutionData as Array<{
              documentId?: string
              solution?: { documentId?: string }
            }>
          ).find((sp) => sp.solution?.documentId === id)?.documentId ?? null
      }

      try {
        const { data } = effectivePortfolioId
          ? meta?.portfolioUpdated
            ? await updateSharedPortfolioSolution({
                variables: {
                  id: portfolioSolutionId || '',
                  category: followType || '',
                  followingSolutionNote: null,
                },
              })
            : await addSolutionToSharedPortfolio({
                variables: followSolutionVariables as IAddSolutionToSharedPortfolioMutationVariables,
              })
          : followType == 'remove'
            ? await followSharedPortfolioSolution({
                variables: followSolutionVariables as IFollowSharedPortfolioSolutionMutationVariables,
              })
            : await addSolutionToSharedPortfolio({
                variables: followSolutionVariables as IAddSolutionToSharedPortfolioMutationVariables,
              })

        const isMutationSuccessful = effectivePortfolioId
          ? meta?.portfolioUpdated
            ? !!(data as IUpdateSharedPortfolioSolutionMutation)?.updateSharedPortfolioSolution
            : !!(data as IAddSolutionToSharedPortfolioMutation)?.addSolutionToSharedPortfolio
          : followType == 'remove'
            ? !!(data as IFollowSharedPortfolioSolutionMutation)?.followSharedPortfolioSolution
            : !!(data as IAddSolutionToSharedPortfolioMutation)?.addSolutionToSharedPortfolio
        if (isMutationSuccessful) {
          if (!isPremium) {
            mutationIsFollow = !!(data as IAddSolutionToSharedPortfolioMutation)?.addSolutionToSharedPortfolio
              ?.attributes?.solution?.documentId
          }

          const shouldRefreshPrivatePortfolioSlug = !hasPremiumOrganization && mutationIsFollow
          if (shouldRefreshPrivatePortfolioSlug && typeof window !== 'undefined') {
            window.dispatchEvent(new Event('private-portfolio:refresh-slug'))
          }

          pendingFollowActionRef.current = mutationIsFollow ? 'follow' : 'unfollow'

          const isBatchIntermediate = meta?.isLastInBatch === false
          const batchSize = meta?.batchSize ?? 1

          if (isBatchIntermediate) {
            if (mutationIsFollow) {
              setFollowing(true)
            }
          } else {
            let finalFollowType: string | null = null

            if (mutationIsFollow && batchSize > 1) {
              finalFollowType = 'Following'
            } else if (mutationIsFollow && isPortfolioTypeUpdate) {
              const matchesUpdatedPortfolio = (entryPortfolioId: string | null | undefined) => {
                if (effectivePortfolioId === '') {
                  return entryPortfolioId == null || entryPortfolioId === '' || entryPortfolioId === 'private_portfolio'
                }
                return String(entryPortfolioId ?? '') === String(effectivePortfolioId)
              }
              const updatedEntries = portfolioEntries.map((entry) =>
                matchesUpdatedPortfolio(entry?.portfolioId)
                  ? { ...entry, followingType: followType || entry.followingType }
                  : entry,
              )
              setPortfolioEntries(updatedEntries)
              const portfolioEntriesForLabel = updatedEntries.map((entry) => ({
                portfolioId: entry.portfolioId ?? null,
                followingType: entry.followingType ?? null,
              }))
              const { label } = resolveFollowStateFromPortfolioEntries(portfolioEntriesForLabel)
              finalFollowType = label || followType || localFollowTypeName || null
            } else if (mutationIsFollow && isAdditiveSharedPortfolio) {
              const pickerOption = portfolioPickerOptions.find((p) => p.id === effectivePortfolioId)
              const newEntry = {
                solutionId: id,
                id: `${id}-shared-${effectivePortfolioId}`,
                followingType: followType || '',
                portfolioId: effectivePortfolioId,
                name: pickerOption && 'title' in pickerOption ? pickerOption.title : '',
                slug: pickerOption && 'slug' in pickerOption ? pickerOption.slug || '' : '',
                members: '',
                organizationId: null,
                sharedPortfolio: effectivePortfolioId,
              }

              const alreadyExists = portfolioEntries.some(
                (entry) => String(entry?.portfolioId || '') === String(effectivePortfolioId),
              )
              const updatedEntries = alreadyExists ? portfolioEntries : [...portfolioEntries, newEntry]
              setPortfolioEntries(filterPortfolioEntriesForUser(updatedEntries))

              const portfolioEntriesForLabel = updatedEntries.map((entry) => ({
                portfolioId: entry.portfolioId ?? null,
                followingType: entry.followingType ?? null,
              }))
              const { label } = resolveFollowStateFromPortfolioEntries(portfolioEntriesForLabel)
              finalFollowType = label || 'Following'
            } else {
              finalFollowType = mutationIsFollow ? followType || localFollowTypeName || null : null
            }

            applyFollowUiState(mutationIsFollow, finalFollowType)
          }

          if (!mutationIsFollow && onRefresh && typeof onRefresh === 'function') {
            await timeout(1000)
            onRefresh(false)
          }

          const showSuccessToast = meta?.isLastInBatch !== false
          const isPortfolioStyleFollow =
            canUseSharedPortfolio &&
            (['Portfolio', 'Reviewed', 'Watching'].includes(followType || '') || localFollowTypeName != null)
          if (showSuccessToast) {
            if (isPortfolioStyleFollow) {
              dispatch(
                showToast(
                  mutationIsFollow ? 'Added to your Portfolio' : 'Removed from your Portfolio',
                  ['my-private-portfolio', 'my-following', 'my-shared-portfolio', 'custom-tag'].includes(isFrom)
                    ? 'confirm_only_no_link'
                    : 'confirm',
                  { viewAllHref: '/my-portfolios' },
                ),
              )
              if (typeof refreshFollowVendorList === 'function') {
                await refreshFollowVendorList()
              } else if (onRefresh && typeof onRefresh === 'function') {
                onRefresh(true)
              }
            } else {
              let msg = ''

              if (isPremium) {
                if (mutationIsFollow) {
                  msg = 'Added to your Portfolio'
                } else {
                  msg = 'Removed from your Portfolio'
                }
              } else {
                if (mutationIsFollow) {
                  msg = 'Added to your followed solutions'
                } else {
                  msg = 'Removed from your followed solutions'
                }
              }

              let privatePortfolioSlug = getPrivatePortfolioSlugForCurrentUser()
              if (!privatePortfolioSlug) {
                privatePortfolioSlug = await fetchAndStorePrivatePortfolioSlug(mutationIsFollow)
              }
              dispatch(
                showToast(msg, isFrom !== 'my-following' ? 'confirm' : 'confirm_only_no_link', {
                  viewAllHref: privatePortfolioSlug ? `/my-followings/${privatePortfolioSlug}` : '/my-followings/',
                }),
              )
            }
          }
          if (MIXPANEL_ENABLED) {
            if (mutationIsFollow) {
              mixpanel.track('follow_product', {
                solutionType,
                id,
                displayName,
                isEnhanced,
              })
            } else {
              mixpanel.track('unfollow_product', {
                solutionType,
                id,
                displayName,
                isEnhanced,
              })
            }
          }
        }
      } catch (error) {
        const errorMessage =
          (
            error as {
              graphQLErrors?: Array<{ message?: string }>
              networkError?: { message?: string }
              message?: string
            }
          )?.graphQLErrors?.[0]?.message ||
          (error as { networkError?: { message?: string } })?.networkError?.message ||
          (error as { message?: string })?.message ||
          'An error occurred while processing your request. Please try again.'

        dispatch(showToast(errorMessage, 'error'))
      }
    }
  }

  function timeout(delay: number) {
    return new Promise((res) => setTimeout(res, delay))
  }

  const handleCloseSignUp = () => {
    setShowSignUp(false)
  }

  const handleCloseShowFollowType = () => {
    setShowFollowType(false)
  }

  const handleFollowType = async (
    type: 'Portfolio' | 'Reviewed' | 'Watching',
    portfolioId: string = '',
    meta?: FollowSolutionMeta,
  ) => {
    if (meta?.isLastInBatch !== false) {
      setShowFollowType(false)
    }

    // if (portfolioId && portfolioId !== '' && portfolioId === 'private_portfolio') {
    //   portfolioId = ''
    //   // setFollowing(false)
    // }

    await onHandleFollow(type, portfolioId, meta)
  }

  const followPortfolioNames = useMemo(() => {
    const names: string[] = []
    const normalizedSolutionId = String(id || '')

    if (localFollowTypeName) {
      names.push('My Portfolio')
    }

    portfolioEntries.forEach((entry: { solutionId?: string | number; name?: string } | null | undefined) => {
      if (!entry) return
      if (String(entry.solutionId || '') !== normalizedSolutionId) return
      if (entry.name) {
        names.push(entry.name)
      }
    })

    return [...new Set(names)]
  }, [id, localFollowTypeName, portfolioEntries])

  const canShowFollowHoverPopup =
    following === true &&
    isPremium === true &&
    hasPremiumOrganization &&
    Boolean(localFollowTypeName) &&
    followPortfolioNames.length > 0
  const canOpenFollowPortfolioPopup = canShowFollowHoverPopup && isFrom === '' && portfolioEntries.length > 0
  const showPortfolioToggle = canOpenFollowPortfolioPopup && !canUseHoverInteraction

  useEffect(() => {
    const closeWhenAnotherPopupOpens = (event: Event) => {
      const customEvent = event as CustomEvent<{ instanceId?: string }>
      if (customEvent.detail?.instanceId !== hoverPopupInstanceId) {
        setShowFollowHoverPopup(false)
      }
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('follow-button-hover-popup-open', closeWhenAnotherPopupOpens)
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('follow-button-hover-popup-open', closeWhenAnotherPopupOpens)
      }
    }
  }, [hoverPopupInstanceId])

  const onHandleUnfollowSolution = async (obj: any) => {
    try {
      const isSharedPortfolioEntry = typeof obj?.slug === 'string' && obj.slug.trim() !== ''
      let mutationSucceeded = false

      if (isSharedPortfolioEntry) {
        const sharedPortfolioVariables: IFollowSharedPortfolioSolutionMutationVariables = {
          portfolioSlug: obj.slug,
          solutionId: id,
          follow: false,
          updateType: 'Remove',
        }
        const { data } = await followSharedPortfolioSolution({
          variables: sharedPortfolioVariables,
        })
        mutationSucceeded = !!(data as IFollowSharedPortfolioSolutionMutation)?.followSharedPortfolioSolution
      } else {
        const { data } = await followSolution({
          variables: {
            id: id || '',
            follow: false,
          },
        })
        mutationSucceeded = !!(data as IFollowSolutionMutation)?.followSolution
      }

      if (!mutationSucceeded) {
        return
      }

      const updatedEntries = portfolioEntries.filter((entry) => String(entry?.id || '') !== String(obj?.id || ''))
      setPortfolioEntries(filterPortfolioEntriesForUser(updatedEntries))

      if (updatedEntries.length === 0) {
        pendingFollowActionRef.current = 'unfollow'
        applyFollowUiState(false, null)
      } else {
        pendingFollowActionRef.current = null
        const portfolioEntriesForLabel = updatedEntries.map((entry) => ({
          portfolioId: entry.portfolioId ?? null,
          followingType: entry.followingType ?? null,
        }))
        const { label } = resolveFollowStateFromPortfolioEntries(portfolioEntriesForLabel)
        applyFollowUiState(true, label || 'Following')
      }

      dispatch(
        showToast(
          `Removed from ${obj?.name || 'Portfolio'}`,
          isFrom !== 'my-following' ? 'confirm' : 'confirm_only_no_link',
        ),
      )

      await timeout(300)
      if (isSharedPortfolioEntry && typeof refreshFollowVendorList === 'function') {
        await refreshFollowVendorList()
      } else if (onRefresh && typeof onRefresh === 'function') {
        onRefresh(true)
      }
    } catch (error) {
      const errorMessage =
        (
          error as {
            graphQLErrors?: Array<{ message?: string }>
            networkError?: { message?: string }
            message?: string
          }
        )?.graphQLErrors?.[0]?.message ||
        (error as { networkError?: { message?: string } })?.networkError?.message ||
        (error as { message?: string })?.message ||
        'An error occurred while processing your request. Please try again.'

      dispatch(showToast(errorMessage, 'error'))
    }
  }
  const handleHoverAreaMouseEnter = () => {
    if (!canUseHoverInteraction) return
    cancelHoverClose()
    if (canOpenFollowPortfolioPopup) {
      setShowFollowHoverPopup(true)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('follow-button-hover-popup-open', {
            detail: { instanceId: hoverPopupInstanceId },
          }),
        )
      }
    }
  }

  const handleHoverAreaMouseLeave = (event: React.MouseEvent) => {
    if (!canUseHoverInteraction) return
    const related = event.relatedTarget
    if (related instanceof Node && hoverPopupRef.current?.contains(related)) {
      return
    }
    scheduleHoverClose()
  }

  const toggleFollowPortfolioPopup = () => {
    if (!canOpenFollowPortfolioPopup) return
    setShowFollowHoverPopup((isOpen) => {
      const nextOpen = !isOpen
      if (nextOpen && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('follow-button-hover-popup-open', {
            detail: { instanceId: hoverPopupInstanceId },
          }),
        )
      }
      return nextOpen
    })
  }

  useEffect(() => () => cancelHoverClose(), [])

  useEffect(() => {
    if (!showFollowHoverPopup) return
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target
      if (hoverPopupRef.current && target instanceof Node && !hoverPopupRef.current.contains(target)) {
        setShowFollowHoverPopup(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showFollowHoverPopup])

  const handleFollowButtonClick = () => {
    if (isPremium && !hasPremiumOrganization) {
      onHandleFollow('remove')
    } else {
      onHandleFollow('', sharedPortfolioId)
    }
  }

  return (
    <div
      ref={hoverPopupRef}
      className={cn('relative flex', showPortfolioToggle && isButton && 'flex-row items-center', className)}
      onMouseEnter={handleHoverAreaMouseEnter}
      onMouseLeave={handleHoverAreaMouseLeave}
    >
      {isButton ? (
        <button
          className="box-border h-[42px] px-[16px] py-[8px] w-full border-solid border-[1px] border-mist rounded-[25px] bg-white items-center"
          onClick={handleFollowButtonClick}
        >
          <div className="flex flex-row items-center self-center justify-center">
            {following ? (
              <IonIcon
                name="star"
                className="flex items-center text-[#104268] text-[1.3rem] mr-[5px] h-[16px] w-[16px]"
              />
            ) : (
              <IonIcon
                name="star-outline"
                className="flex items-center text-[#104268] text-[1.3rem] mr-[5px]  h-[16px] w-[16px]"
              />
            )}
            <span className="flex items-center h-full text-darkgray font-secondary text-[14px] font-[500]">
              {/* {following ? 'Following' : 'Follow'} */}
              {following
                ? isPremium && localFollowTypeName != null
                  ? localFollowTypeName || 'Following'
                  : 'Following'
                : 'Follow'}
            </span>
          </div>
        </button>
      ) : showPortfolioToggle ? (
        <div className="relative ml-[1rem] inline-flex h-[1.5rem] items-center">
          <div className="inline-flex h-[1.5rem] items-center">
            <button
              type="button"
              className="inline-flex h-[1.5rem] shrink-0 flex-row items-center"
              onClick={handleFollowButtonClick}
            >
              {following ? (
                <IonIcon name="star" className="text-navy !h-[16px] !w-[16px]" />
              ) : (
                <IonIcon name="star-outline" className="text-textdark opacity-40 !h-[16px] !w-[16px]" />
              )}
              <span className="pl-2 pr-0.5 font-secondary font-medium text-[14px] text-darkgray">
                {following
                  ? isPremium && localFollowTypeName != null
                    ? localFollowTypeName || 'Following'
                    : 'Following'
                  : 'Follow'}
              </span>
            </button>
            <button
              type="button"
              className="inline-flex h-[1.5rem] w-5 shrink-0 items-center justify-center p-0 text-navy"
              aria-expanded={showFollowHoverPopup}
              aria-label={showFollowHoverPopup ? 'Hide portfolio memberships' : 'Show portfolio memberships'}
              onClick={(event) => {
                event.stopPropagation()
                toggleFollowPortfolioPopup()
              }}
            >
              <IonIcon
                name={showFollowHoverPopup ? 'chevron-up-outline' : 'chevron-down-outline'}
                className="!h-[14px] !w-[14px] transition-transform duration-200"
              />
            </button>
          </div>
        </div>
      ) : (
        <button
          className={cn('flex flex-row items-center h-[1.5rem] ml-[1rem] w-[92px]')}
          onClick={handleFollowButtonClick}
        >
          {following ? (
            <IonIcon name="star" className="text-navy !h-[16px] !w-[16px]" />
          ) : (
            <IonIcon name="star-outline" className="text-textdark opacity-40  !h-[16px] !w-[16px]" />
          )}

          <span className="pl-2 font-secondary font-medium text-[14px] text-darkgray">
            {following
              ? isPremium && localFollowTypeName != null
                ? localFollowTypeName || 'Following'
                : 'Following'
              : 'Follow'}
          </span>
        </button>
      )}

      {showPortfolioToggle && isButton && (
        <button
          type="button"
          className="inline-flex h-[44px] w-5 shrink-0 items-center justify-center p-0 text-navy"
          aria-expanded={showFollowHoverPopup}
          aria-label={showFollowHoverPopup ? 'Hide portfolio memberships' : 'Show portfolio memberships'}
          onClick={(event) => {
            event.stopPropagation()
            toggleFollowPortfolioPopup()
          }}
        >
          <IonIcon
            name={showFollowHoverPopup ? 'chevron-up-outline' : 'chevron-down-outline'}
            className="!h-[14px] !w-[14px] transition-transform duration-200"
          />
        </button>
      )}

      {canOpenFollowPortfolioPopup && showFollowHoverPopup && (
        <div
          ref={portfolioPopupRef}
          className={cn(
            'shared-portfolio-hover-popup-container absolute right-0  h-auto min-w-[151px] w-max rounded-[8px] bg-[#FFFFFF] px-[16px] py-[8px] shadow-[0_4px_10px_0_#202A3533] z-20',
            "before:absolute before:-top-3 before:left-0 before:right-0 before:h-3 before:content-['']",
            styleParameters ? styleParameters : 'top-[42px]',
          )}
        >
          <ul className="flex flex-col gap-[8px] bg-[#FFFFFF]">
            {portfolioEntries.map((obj: any) => (
              <li
                key={`${id}-${obj?.portfolioId}`}
                className="w-auto h-[40px] overflow-hidden flex flex-row items-center justify-between whitespace-nowrap text-[#011D58] font-poppins font-medium text-[14px] leading-[40px] tracking-[0px]"
              >
                <Link
                  target="_blank"
                  href={
                    obj?.isSharedPortfolio === false ||
                    (typeof obj?.slug === 'string' && obj.slug.includes('my-portfolio'))
                      ? `/my-followings/${obj?.slug}`
                      : obj?.slug
                        ? `/my-shared-portfolios/${obj?.slug}`
                        : '/my-followings'
                  }
                >
                  <label className="cursor-pointer font-primary font-medium text-[16px] text-[#011d58]">
                    {obj?.name}
                  </label>
                </Link>
                <button
                  type="button"
                  className="box-border cursor-pointer inline-flex w-max shrink-0 items-center justify-center self-start whitespace-nowrap font-primary font-normal tracking-normal text-textdark opacity-100"
                  style={{
                    marginTop: FIGMA.titleToPillGapPx,
                    minWidth: FIGMA.visibilityPillMinWidthPx,
                    minHeight: FIGMA.visibilityPillMinHeightPx,
                    paddingTop: FIGMA.visibilityPillPaddingY,
                    paddingRight: FIGMA.visibilityPillPaddingX,
                    paddingBottom: FIGMA.visibilityPillPaddingY,
                    paddingLeft: FIGMA.visibilityPillPaddingX,
                    borderRadius: FIGMA.visibilityPillRadiusPx,
                    backgroundColor: FIGMA.visibilityPillBg,
                    fontSize: FIGMA.articleCopy13Px,
                    lineHeight: `${FIGMA.visibilityPillMinHeightPx - 2 * FIGMA.visibilityPillPaddingY}px`,
                    letterSpacing: 0,
                    marginLeft: '10px',
                  }}
                  onClick={() => onHandleUnfollowSolution(obj)}
                >
                  <div className="inline-flex cursor-pointer items-start gap-2 font-primary font-medium text-[13px] text-darkgray">
                    <span
                      className="relative shrink-0"
                      style={{
                        width: FIGMA.unfollowCheckboxFramePx,
                        height: FIGMA.unfollowCheckboxFramePx,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked
                        readOnly
                        tabIndex={-1}
                        aria-hidden
                        className="absolute m-0 shrink-0 cursor-pointer appearance-auto p-0 opacity-100"
                        style={{
                          top: FIGMA.unfollowCheckboxInsetPx,
                          left: FIGMA.unfollowCheckboxInsetPx,
                          width: FIGMA.unfollowCheckboxSizePx,
                          height: FIGMA.unfollowCheckboxSizePx,
                          accentColor: FIGMA.unfollowCheckboxAccent,
                        }}
                      />
                    </span>
                    {obj?.followingType || obj?.category || 'Following'}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <SignUpModal isOpen={showSignUp} onClose={handleCloseSignUp} />
      {isPremium && (!following || isFrom === '') && (
        <PremiumFollowSolution
          isOpen={showFollowType}
          onClose={handleCloseShowFollowType}
          followSolution={handleFollowType}
          portfolioPickerOptions={modalPortfolioPickerOptions}
          onSubmitSharedPortfolio={onSubmitSharedPortfolio}
          sharedPortfolioId={sharedPortfolioId}
          sharedPortfolioSolutions={sharedPortfolioSolutions || []}
        />
      )}
    </div>
  )
}
